import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { FiSliders, FiThumbsUp, FiX } from 'react-icons/fi';
import MsvSelect from '../MsvSelect/MsvSelect';
import {
    COLLECTIBLE_DATE_SORT_OPTIONS,
    isMostLikedSort,
    normalizeDateSort,
} from '../../utils/vaultCollectibleSort';
import { useAnimatedPresence } from '../../hooks/useAnimatedPresence';
import { useBrowseOverlay } from '../../hooks/useBrowseOverlay';
import {
    buildVaultCollectibleFilterChips,
    countVaultDrawerFilters,
    DEFAULT_RACE_USED_MAIN_CATEGORY,
    toggleVaultMainCategory,
    VAULT_RACE_USED_BROWSE_CATEGORIES,
} from '../../utils/vaultBrowseCategories';
import {
    driverIdPairs,
    seriesIdPairs,
    mainCategoriesIdPairs,
    raceUsedMainCategoriesIdPairs,
    DIECASTS_MAIN_CATEGORY,
    diecastSubCategoriesIdPairs,
    raceUsedSubCategoriesIdPairs,
    driverWornSubCategoriesIdPairs,
    sheetMetalSubCategoriesIdPairs,
    teamIssuedSubCategoriesIdPairs,
    diecastYearCategoriesIdPairs,
    diecastVersionCategoriesIdPairs,
    tradingCardYearCategoriesIdPairs,
    tradingCardBrandCategoriesIdPairs,
    tradingCardTypeCategoriesIdPairs,
    heroCardYearCategoriesIdPairs,
    programsTicketsYearCategoriesIdPairs,
} from '../../constants/dropdownIdPairs';

const emptyForm = {
    driverName: '',
    series: '',
    mainCategory: DEFAULT_RACE_USED_MAIN_CATEGORY,
    subCategory: '',
    subSubCategory: '',
    subSubSubCategory: '',
};

const lockedInputStyle = {
    backgroundColor: '#f0f0f0',
    opacity: 0.6,
    cursor: 'not-allowed',
};

const findByName = (options, name, key = 'name') => {
    if (!name || !Array.isArray(options)) return null;
    return options.find((o) => o[key] === name) || null;
};

/**
 * Shared collectible/diecast filter toolbar.
 * Top bar: Driver, Series, Sort, Filters (mobile). Changes apply immediately.
 */
const VaultCollectibleFilterForm = ({
    isDiecastsMode = false,
    formData,
    onFiltersApply,
    sortBy = 'recent',
    onSortChange,
    onClearFilters,
    className = '',
    children,
    browseCategories = null,
}) => {
    const data = { ...emptyForm, ...(formData || {}) };
    if (isDiecastsMode) data.mainCategory = DIECASTS_MAIN_CATEGORY;

    const [showFilters, setShowFilters] = useState(false);
    const [lastDateSort, setLastDateSort] = useState(() => normalizeDateSort(sortBy));

    useEffect(() => {
        if (!isMostLikedSort(sortBy)) {
            setLastDateSort(normalizeDateSort(sortBy));
        }
    }, [sortBy]);

    const handleDateSortChange = (next) => {
        const nextDateSort = normalizeDateSort(next);
        setLastDateSort(nextDateSort);
        onSortChange?.(nextDateSort);
    };

    const handleMostLikedToggle = () => {
        if (isMostLikedSort(sortBy)) {
            onSortChange?.(lastDateSort);
            return;
        }
        onSortChange?.('mostLiked');
    };
    const { mounted: filtersMounted, visible: filtersVisible } = useAnimatedPresence(showFilters);
    const setBrowseOverlay = useBrowseOverlay();
    const drawerBodyRef = useRef(null);

    useEffect(() => {
        if (!showFilters) return;
        drawerBodyRef.current?.scrollTo(0, 0);
    }, [showFilters]);

    useEffect(() => {
        if (!filtersMounted) return undefined;
        document.body.classList.add('msv-filters-drawer-open');
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.classList.remove('msv-filters-drawer-open');
            document.body.style.overflow = previousOverflow;
        };
    }, [filtersMounted]);

    const [drivers, setDrivers] = useState([]);
    const [series, setSeries] = useState([]);
    const [mainCategories, setMainCategories] = useState([]);
    const [diecastSubCategories, setDiecastSubCategories] = useState([]);
    const [raceUsedSubCategories, setRaceUsedSubCategories] = useState([]);
    const [driverWornSubCategories, setDriverWornSubCategories] = useState([]);
    const [sheetMetalSubCategories, setSheetMetalSubCategories] = useState([]);
    const [teamIssuedSubCategories, setTeamIssuedSubCategories] = useState([]);
    const [diecastYearCategories, setDiecastYearCategories] = useState([]);
    const [diecastVersionCategories, setDiecastVersionCategories] = useState([]);
    const [tradingCardYearCategories, setTradingCardYearCategories] = useState([]);
    const [tradingCardBrandCategories, setTradingCardBrandCategories] = useState([]);
    const [tradingCardTypeCategories, setTradingCardTypeCategories] = useState([]);
    const [heroCardYearCategories, setHeroCardYearCategories] = useState([]);
    const [programTicketYearCategories, setProgramTicketYearCategories] = useState([]);

    const [selectedDriver, setSelectedDriver] = useState(null);
    const [selectedSeries, setSelectedSeries] = useState(null);
    const [selectedMainCategory, setSelectedMainCategory] = useState(null);
    const [selectedDiecastSubCategory, setSelectedDiecastSubCategory] = useState(null);
    const [selectedRaceUsedSubCategory, setSelectedRaceUsedSubCategory] = useState(null);
    const [selectedDriverWornSubCategory, setSelectedDriverWornSubCategory] = useState(null);
    const [selectedSheetMetalSubCategory, setSelectedSheetMetalSubCategory] = useState(null);
    const [selectedTeamIssuedSubCategory, setSelectedTeamIssuedSubCategory] = useState(null);
    const [selectedDiecastYearCategory, setSelectedDiecastYearCategory] = useState(null);
    const [selectedDiecastVersionCategory, setSelectedDiecastVersionCategory] = useState(null);
    const [selectedTradingCardYearCategory, setSelectedTradingCardYearCategory] = useState(null);
    const [selectedTradingCardBrandCategory, setSelectedTradingCardBrandCategory] = useState(null);
    const [selectedTradingCardTypeCategory, setSelectedTradingCardTypeCategory] = useState(null);
    const [selectedHeroCardYearCategory, setSelectedHeroCardYearCategory] = useState(null);
    const [selectedProgramTicketYearCategory, setSelectedProgramTicketYearCategory] = useState(null);

    useEffect(() => {
        setDrivers([...driverIdPairs].sort((a, b) =>
            a.fullName.toLowerCase().localeCompare(b.fullName.toLowerCase())
        ));
        setSeries([...seriesIdPairs].sort((a, b) =>
            a.seriesName.toLowerCase().localeCompare(b.seriesName.toLowerCase())
        ));
        setMainCategories(isDiecastsMode
            ? mainCategoriesIdPairs.filter((c) => c.name === DIECASTS_MAIN_CATEGORY)
            : raceUsedMainCategoriesIdPairs);
        setDiecastSubCategories(diecastSubCategoriesIdPairs);
        setRaceUsedSubCategories(raceUsedSubCategoriesIdPairs);
        setDriverWornSubCategories(driverWornSubCategoriesIdPairs);
        setSheetMetalSubCategories(sheetMetalSubCategoriesIdPairs);
        setTeamIssuedSubCategories(teamIssuedSubCategoriesIdPairs);
        setDiecastYearCategories([...diecastYearCategoriesIdPairs].sort((a, b) => parseInt(b.name, 10) - parseInt(a.name, 10)));
        setDiecastVersionCategories([...diecastVersionCategoriesIdPairs].sort((a, b) => a.name.localeCompare(b.name)));
        setTradingCardYearCategories([...tradingCardYearCategoriesIdPairs].sort((a, b) => parseInt(b.name, 10) - parseInt(a.name, 10)));
        setTradingCardBrandCategories(tradingCardBrandCategoriesIdPairs);
        setTradingCardTypeCategories(tradingCardTypeCategoriesIdPairs);
        setHeroCardYearCategories([...heroCardYearCategoriesIdPairs].sort((a, b) => parseInt(b.name, 10) - parseInt(a.name, 10)));
        setProgramTicketYearCategories([...programsTicketsYearCategoriesIdPairs].sort((a, b) => parseInt(b.name, 10) - parseInt(a.name, 10)));
    }, [isDiecastsMode]);

    useEffect(() => {
        setSelectedDriver(findByName(drivers, data.driverName, 'fullName'));
        setSelectedSeries(findByName(series, data.series, 'seriesName'));
        setSelectedMainCategory(isDiecastsMode
            ? null
            : findByName(mainCategories, data.mainCategory));
        setSelectedDiecastSubCategory(findByName(diecastSubCategories, data.subCategory));
        setSelectedRaceUsedSubCategory(findByName(raceUsedSubCategories, data.subCategory));
        setSelectedDiecastYearCategory(findByName(diecastYearCategories, data.subSubCategory));
        setSelectedSheetMetalSubCategory(findByName(sheetMetalSubCategories, data.subSubCategory));
        setSelectedTeamIssuedSubCategory(findByName(teamIssuedSubCategories, data.subSubCategory));
        setSelectedDriverWornSubCategory(findByName(driverWornSubCategories, data.subSubCategory));
        setSelectedTradingCardYearCategory(findByName(tradingCardYearCategories, data.subCategory));
        setSelectedTradingCardBrandCategory(findByName(tradingCardBrandCategories, data.subSubCategory));
        setSelectedTradingCardTypeCategory(findByName(tradingCardTypeCategories, data.subSubSubCategory));
        setSelectedDiecastVersionCategory(findByName(diecastVersionCategories, data.subSubSubCategory));
        setSelectedHeroCardYearCategory(findByName(heroCardYearCategories, data.subCategory));
        setSelectedProgramTicketYearCategory(findByName(programTicketYearCategories, data.subCategory));
    }, [
        data.driverName,
        data.series,
        data.mainCategory,
        data.subCategory,
        data.subSubCategory,
        data.subSubSubCategory,
        isDiecastsMode,
        drivers,
        series,
        mainCategories,
        diecastSubCategories,
        raceUsedSubCategories,
        diecastYearCategories,
        sheetMetalSubCategories,
        teamIssuedSubCategories,
        driverWornSubCategories,
        tradingCardYearCategories,
        tradingCardBrandCategories,
        tradingCardTypeCategories,
        diecastVersionCategories,
        heroCardYearCategories,
        programTicketYearCategories,
    ]);

    const patchForm = (patch) => {
        const next = { ...data, ...patch };
        onFiltersApply?.(next);
    };

    const mainCatName = selectedMainCategory?.name || '';
    const raceUsedSubName = selectedRaceUsedSubCategory?.name || '';
    const effectiveMainCatName = isDiecastsMode
        ? DIECASTS_MAIN_CATEGORY
        : (mainCatName || data.mainCategory || DEFAULT_RACE_USED_MAIN_CATEGORY);

    const drawerMainCategories = useMemo(
        () => (browseCategories || VAULT_RACE_USED_BROWSE_CATEGORIES).map((cat, index) => ({
            id: cat.name || `vault-cat-${index}`,
            name: cat.name,
            label: cat.label,
        })),
        [browseCategories],
    );

    const drawerMainCat =
        drawerMainCategories.find((cat) => cat.name === effectiveMainCatName)
        ?? drawerMainCategories[0];

    const drawerAc = (options, label, value, onChange, disabled) => (
        <Autocomplete
            disablePortal
            options={options}
            getOptionLabel={(option) => option.label || option.name || ''}
            isOptionEqualToValue={(option, selected) => option?.id === selected?.id}
            value={value}
            onChange={onChange}
            disabled={!!disabled}
            renderInput={(params) => <TextField {...params} label={label} size="small" />}
            sx={{ width: '100%' }}
        />
    );

    const level1Enabled = !isDiecastsMode && !!mainCatName;
    const level1Label =
        mainCatName === 'Trading Cards' ||
        mainCatName === 'Programs / Tickets' ||
        mainCatName === 'Hero Cards / Post Cards'
            ? 'Year'
            : 'Subcategory';
    const level1Options = !level1Enabled
        ? []
        : mainCatName === 'Race Used / Team Issued'
            ? raceUsedSubCategories
            : mainCatName === 'Trading Cards'
                ? tradingCardYearCategories
                : mainCatName === 'Programs / Tickets'
                    ? programTicketYearCategories
                    : mainCatName === 'Hero Cards / Post Cards'
                        ? heroCardYearCategories
                        : [];
    const level1Value = !level1Enabled
        ? null
        : mainCatName === 'Race Used / Team Issued'
            ? selectedRaceUsedSubCategory
            : mainCatName === 'Trading Cards'
                ? selectedTradingCardYearCategory
                : mainCatName === 'Programs / Tickets'
                    ? selectedProgramTicketYearCategory
                    : mainCatName === 'Hero Cards / Post Cards'
                        ? selectedHeroCardYearCategory
                        : null;

    const level2Enabled =
        !isDiecastsMode &&
        ((mainCatName === 'Race Used / Team Issued' &&
            (raceUsedSubName === 'Sheet Metal / Body Panel' ||
                raceUsedSubName === 'Team Issued' ||
                raceUsedSubName === 'Driver Worn')) ||
            (mainCatName === 'Trading Cards' && !!selectedTradingCardYearCategory));
    const level2Label = mainCatName === 'Trading Cards' ? 'Brand' : 'Detail';
    const level2Options = !level2Enabled
        ? []
        : mainCatName === 'Trading Cards'
            ? tradingCardBrandCategories
            : raceUsedSubName === 'Sheet Metal / Body Panel'
                ? sheetMetalSubCategories
                : raceUsedSubName === 'Team Issued'
                    ? teamIssuedSubCategories
                    : raceUsedSubName === 'Driver Worn'
                        ? driverWornSubCategories
                        : [];
    const level2Value = !level2Enabled
        ? null
        : mainCatName === 'Trading Cards'
            ? selectedTradingCardBrandCategory
            : raceUsedSubName === 'Sheet Metal / Body Panel'
                ? selectedSheetMetalSubCategory
                : raceUsedSubName === 'Team Issued'
                    ? selectedTeamIssuedSubCategory
                    : raceUsedSubName === 'Driver Worn'
                        ? selectedDriverWornSubCategory
                        : null;

    const level3Enabled =
        !isDiecastsMode && mainCatName === 'Trading Cards' && !!selectedTradingCardBrandCategory;
    const level3Options = level3Enabled ? tradingCardTypeCategories : [];
    const level3Value = level3Enabled ? selectedTradingCardTypeCategory : null;

    const drawerCategoryCount = buildVaultCollectibleFilterChips(data, isDiecastsMode)
        .filter((chip) => chip.key === 'mainCategory' || chip.key === 'subCategory')
        .length;
    const drawerFilterCount = countVaultDrawerFilters(data, isDiecastsMode) + drawerCategoryCount;

    const clearCascadeSelections = () => {
        setSelectedRaceUsedSubCategory(null);
        setSelectedSheetMetalSubCategory(null);
        setSelectedDriverWornSubCategory(null);
        setSelectedTeamIssuedSubCategory(null);
        setSelectedDiecastSubCategory(null);
        setSelectedDiecastYearCategory(null);
        setSelectedDiecastVersionCategory(null);
        setSelectedTradingCardYearCategory(null);
        setSelectedTradingCardBrandCategory(null);
        setSelectedTradingCardTypeCategory(null);
        setSelectedHeroCardYearCategory(null);
        setSelectedProgramTicketYearCategory(null);
    };

    const handleLevel1Change = (val) => {
        clearCascadeSelections();
        if (mainCatName === 'Race Used / Team Issued') {
            setSelectedRaceUsedSubCategory(val);
        } else if (mainCatName === 'Trading Cards') {
            setSelectedTradingCardYearCategory(val);
        } else if (mainCatName === 'Programs / Tickets') {
            setSelectedProgramTicketYearCategory(val);
        } else if (mainCatName === 'Hero Cards / Post Cards') {
            setSelectedHeroCardYearCategory(val);
        }
        patchForm({
            subCategory: val?.name || '',
            subSubCategory: '',
            subSubSubCategory: '',
        });
    };

    const handleLevel2Change = (val) => {
        setSelectedSheetMetalSubCategory(null);
        setSelectedDriverWornSubCategory(null);
        setSelectedTeamIssuedSubCategory(null);
        setSelectedTradingCardBrandCategory(null);
        setSelectedTradingCardTypeCategory(null);

        if (mainCatName === 'Trading Cards') {
            setSelectedTradingCardBrandCategory(val);
        } else if (raceUsedSubName === 'Sheet Metal / Body Panel') {
            setSelectedSheetMetalSubCategory(val);
        } else if (raceUsedSubName === 'Team Issued') {
            setSelectedTeamIssuedSubCategory(val);
        } else if (raceUsedSubName === 'Driver Worn') {
            setSelectedDriverWornSubCategory(val);
        }
        patchForm({
            subSubCategory: val?.name || '',
            subSubSubCategory: '',
        });
    };

    const handleClearFilters = (e) => {
        e?.preventDefault?.();
        setSelectedDriver(null);
        setSelectedSeries(null);
        setSelectedMainCategory(null);
        clearCascadeSelections();
        const cleared = {
            ...emptyForm,
            mainCategory: isDiecastsMode ? DIECASTS_MAIN_CATEGORY : DEFAULT_RACE_USED_MAIN_CATEGORY,
        };
        onClearFilters?.(cleared);
        setShowFilters(false);
    };

    const filterDrawer = useMemo(() => {
        if (!filtersMounted) return null;

        return (
            <div className={`msv-browse-drawer-root${filtersVisible ? ' is-open' : ''}`}>
                <button
                    type="button"
                    className="msv-browse-drawer-backdrop"
                    aria-label="Close filters"
                    onClick={() => setShowFilters(false)}
                />
                <aside className="msv-browse-drawer" role="dialog" aria-label="Filters">
                    <div className="msv-browse-drawer-header">
                        <h2>Filters</h2>
                        <button
                            type="button"
                            className="msv-browse-drawer-close"
                            onClick={() => setShowFilters(false)}
                            aria-label="Close"
                        >
                            <FiX />
                        </button>
                    </div>

                    <div className="msv-browse-drawer-body" ref={drawerBodyRef}>
                        <div className="msv-browse-drawer-category-filters">
                            {drawerAc(
                                drawerMainCategories,
                                'Category',
                                drawerMainCat,
                                (_, val) => {
                                    onFiltersApply?.(toggleVaultMainCategory(
                                        data,
                                        val?.name ?? '',
                                        isDiecastsMode,
                                    ));
                                },
                            )}

                            {(isDiecastsMode || effectiveMainCatName === DIECASTS_MAIN_CATEGORY) &&
                                drawerAc(
                                    diecastSubCategories,
                                    'Scale / type',
                                    selectedDiecastSubCategory,
                                    (_, val) => {
                                        setSelectedDiecastSubCategory(val);
                                        setSelectedDiecastYearCategory(null);
                                        setSelectedDiecastVersionCategory(null);
                                        patchForm({
                                            subCategory: val?.name || '',
                                            subSubCategory: '',
                                            subSubSubCategory: '',
                                        });
                                    },
                                )}

                            {!isDiecastsMode && effectiveMainCatName === DEFAULT_RACE_USED_MAIN_CATEGORY &&
                                drawerAc(
                                    raceUsedSubCategories,
                                    'Type',
                                    selectedRaceUsedSubCategory,
                                    (_, val) => {
                                        setSelectedRaceUsedSubCategory(val);
                                        setSelectedSheetMetalSubCategory(null);
                                        setSelectedDriverWornSubCategory(null);
                                        setSelectedTeamIssuedSubCategory(null);
                                        patchForm({
                                            subCategory: val?.name || '',
                                            subSubCategory: '',
                                        });
                                    },
                                )}
                        </div>

                        {!isDiecastsMode && mainCatName === 'Race Used / Team Issued' && (
                            <Autocomplete
                                options={level2Options}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={level2Value}
                                onChange={(e, val) => handleLevel2Change(val)}
                                disabled={!level2Enabled}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={level2Label}
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            style: !level2Enabled ? lockedInputStyle : {},
                                        }}
                                    />
                                )}
                            />
                        )}

                        {!isDiecastsMode && (
                            (mainCatName === 'Trading Cards'
                                || mainCatName === 'Programs / Tickets'
                                || mainCatName === 'Hero Cards / Post Cards') && (
                                <Autocomplete
                                    options={level1Options}
                                    getOptionLabel={(option) => option.name}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    value={level1Value}
                                    onChange={(e, val) => handleLevel1Change(val)}
                                    disabled={!level1Enabled}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={level1Label}
                                            size="small"
                                            InputProps={{
                                                ...params.InputProps,
                                                style: !level1Enabled ? lockedInputStyle : {},
                                            }}
                                        />
                                    )}
                                />
                            )
                        )}

                        {!isDiecastsMode && mainCatName === 'Trading Cards' && (
                            <Autocomplete
                                options={level2Options}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={level2Value}
                                onChange={(e, val) => handleLevel2Change(val)}
                                disabled={!level2Enabled}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={level2Label}
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            style: !level2Enabled ? lockedInputStyle : {},
                                        }}
                                    />
                                )}
                            />
                        )}

                        {!isDiecastsMode && mainCatName === 'Trading Cards' && (
                            <Autocomplete
                                options={level3Options}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={level3Value}
                                onChange={(e, val) => {
                                    setSelectedTradingCardTypeCategory(val);
                                    patchForm({ subSubSubCategory: val?.name || '' });
                                }}
                                disabled={!level3Enabled}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Type"
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            style: !level3Enabled ? lockedInputStyle : {},
                                        }}
                                    />
                                )}
                            />
                        )}

                        {isDiecastsMode && (
                            <Autocomplete
                                options={diecastYearCategories}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={selectedDiecastYearCategory}
                                onChange={(e, val) => {
                                    setSelectedDiecastYearCategory(val);
                                    setSelectedDiecastVersionCategory(null);
                                    patchForm({
                                        subSubCategory: val?.name || '',
                                        subSubSubCategory: '',
                                    });
                                }}
                                disabled={!selectedDiecastSubCategory}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Year"
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            style: !selectedDiecastSubCategory ? lockedInputStyle : {},
                                        }}
                                    />
                                )}
                            />
                        )}

                        {isDiecastsMode && (
                            <Autocomplete
                                options={diecastVersionCategories}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={selectedDiecastVersionCategory}
                                onChange={(e, val) => {
                                    setSelectedDiecastVersionCategory(val);
                                    patchForm({ subSubSubCategory: val?.name || '' });
                                }}
                                disabled={!selectedDiecastYearCategory}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Version"
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            style: !selectedDiecastYearCategory ? lockedInputStyle : {},
                                        }}
                                    />
                                )}
                            />
                        )}
                    </div>

                    <div className="msv-browse-drawer-footer">
                        <button
                            type="button"
                            className="msv-browse-search-btn msv-browse-clear-btn"
                            onClick={handleClearFilters}
                        >
                            Clear filters
                        </button>
                        <button
                            type="button"
                            className="msv-browse-search-btn"
                            onClick={() => setShowFilters(false)}
                        >
                            Done
                        </button>
                    </div>
                </aside>
            </div>
        );
    }, [
        filtersMounted,
        filtersVisible,
        data,
        isDiecastsMode,
        drawerMainCategories,
        drawerMainCat,
        effectiveMainCatName,
        mainCatName,
        diecastSubCategories,
        selectedDiecastSubCategory,
        raceUsedSubCategories,
        selectedRaceUsedSubCategory,
        level1Enabled,
        level1Label,
        level1Options,
        level1Value,
        level2Enabled,
        level2Label,
        level2Options,
        level2Value,
        level3Enabled,
        level3Options,
        level3Value,
        diecastYearCategories,
        selectedDiecastYearCategory,
        diecastVersionCategories,
        selectedDiecastVersionCategory,
        onFiltersApply,
    ]);

    useEffect(() => {
        if (!setBrowseOverlay) return undefined;
        setBrowseOverlay(filterDrawer);
        return () => setBrowseOverlay(null);
    }, [filterDrawer, setBrowseOverlay]);

    return (
        <>
            <div className={`msv-browse-filter-form ${className}`.trim()}>
                <div className="msv-browse-filter-row">
                    <Autocomplete
                        disablePortal
                        options={drivers}
                        getOptionLabel={(option) => option.fullName}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={selectedDriver}
                        onChange={(e, val) => {
                            setSelectedDriver(val);
                            if (!val) setSelectedSeries(null);
                            patchForm({
                                driverName: val?.fullName || '',
                                ...(val ? {} : { series: data.series }),
                            });
                        }}
                        disabled={selectedSeries !== null}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Driver"
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    style: selectedSeries ? lockedInputStyle : {},
                                }}
                            />
                        )}
                        sx={{ width: '100%', marginRight: 0 }}
                    />

                    <Autocomplete
                        disablePortal
                        options={series}
                        getOptionLabel={(option) => option.seriesName}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={selectedSeries}
                        onChange={(e, val) => {
                            setSelectedSeries(val);
                            if (!val) setSelectedDriver(null);
                            patchForm({
                                series: val?.seriesName || '',
                                ...(val ? {} : { driverName: data.driverName }),
                            });
                        }}
                        disabled={selectedDriver !== null}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Series"
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    style: selectedDriver ? lockedInputStyle : {},
                                }}
                            />
                        )}
                        sx={{ width: '100%', marginRight: 0 }}
                    />

                    <MsvSelect
                        id="msv-vault-collectible-sort"
                        label="Sort"
                        className="is-row is-compact msv-sort-field"
                        value={isMostLikedSort(sortBy) ? lastDateSort : normalizeDateSort(sortBy)}
                        options={COLLECTIBLE_DATE_SORT_OPTIONS}
                        disabled={isMostLikedSort(sortBy)}
                        onChange={handleDateSortChange}
                    />

                    <button
                        type="button"
                        className={`msv-browse-most-liked-btn${isMostLikedSort(sortBy) ? ' is-active' : ''}`}
                        aria-pressed={isMostLikedSort(sortBy)}
                        onClick={handleMostLikedToggle}
                    >
                        <FiThumbsUp aria-hidden />
                        Most liked
                    </button>

                    <button
                        type="button"
                        className={`msv-browse-filters-btn${showFilters || drawerFilterCount ? ' is-active' : ''}`}
                        onClick={() => setShowFilters(true)}
                    >
                        <FiSliders aria-hidden />
                        Filters{drawerFilterCount ? ` · ${drawerFilterCount}` : ''}
                    </button>
                </div>
                {children}
            </div>
        </>
    );
};

export default VaultCollectibleFilterForm;
export { emptyForm as emptyCollectibleFilterForm, DIECASTS_MAIN_CATEGORY };
